import { ReactElement } from 'react';

/**
 * log an warn indicating that a function is not implemented.
 */
export function unimplemented(): void {
  console.warn('Not implemented');
}

/**
 * Iterates through the nested elements and return the text content
 */
export function getTextContent(elem: ReactElement | string): string {
  if (!elem) {
    return '';
  }

  if (typeof elem === 'string') {
    return elem;
  }

  const children = elem.props && elem.props.children;

  if (children instanceof Array) {
    return children.map(getTextContent).join('');
  }
  return getTextContent(children);
}

export function genRandomString(length: number) {
  const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const charLength = chars.length;
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * charLength));
  }
  return result;
}

/**
 * Converts an object to a base64 encoded string
 * @param object
 * @returns base64 encoded string
 **/
export const objectToToken = (object: object) => {
  const stringifiedObject = JSON.stringify(object);

  const base64 = window.btoa(encodeURIComponent(stringifiedObject));
  return base64;
};

/**
 * Converts a base64 encoded string to an object
 * @param token
 * @returns object
 * */
export const tokenToObject = (token: string) => {
  const decodedString = decodeURIComponent(window.atob(token));
  const object = JSON.parse(decodedString);
  return object;
};

/**
 * Check if JSON string has balanced brackets
 * @param jsonString
 * @returns boolean
 */

export function isBalanced(jsonString: string): boolean {
  const stack: string[] = [];
  const pairs = {
    '}': '{',
    ']': '[',
  };

  for (const char of jsonString) {
    if (char === '{' || char === '[') {
      stack.push(char);
    } else if (char === '}' || char === ']') {
      if (stack.length === 0 || stack[stack.length - 1] !== pairs[char]) {
        // Unbalanced or mismatched bracket found
        return false;
      }
      stack.pop(); // Remove the last opening bracket from the stack
    }
  }

  // If the stack is empty, all brackets were balanced
  return stack.length === 0;
}

// todo: write tests for this function
/**
 *
 * @param object
 * @returns sorted `object`
 */
export function sortObjectDeeply<T>(object: T): T {
  if (typeof object !== 'object' || object === null) {
    return object;
  }

  // If it's an array, sort each element
  if (Array.isArray(object)) {
    return object.map(sortObjectDeeply) as T;
  }

  // If it's an object, sort its properties recursively
  const sortedKeys = Object.keys(object).sort();
  const result = {} as T;
  sortedKeys.forEach((key) => {
    const typedKey = key as keyof T;
    result[typedKey] = sortObjectDeeply(object[typedKey]);
  });
  return result;
}

/**
 * Converts an array of objects into a CSV formatted string.
 *
 * Each object's values are extracted and joined into a single line of CSV. Keys are not included in the CSV output.
 * Assumes that all objects in the array have the same keys.
 *
 * @param {Array<{ [key: string]: string }>} objArray - An array of objects where each key-value pair represents a row in the CSV.
 * @returns {string} A CSV formatted string where each line corresponds to an object's values, separated by commas.
 *
 * @example
 * const data = [
 *   { name: 'Alice', age: '30', city: 'New York' },
 *   { name: 'Bob', age: '25', city: 'San Francisco' }
 * ];
 * const csv = objArrayToCSV(data);
 * console.log(csv);
 * // Output:
 * // "Alice,30,New York
 * //  Bob,25,San Francisco"
 */
export const objArrayToCSV = (objArray: Array<{ [key: string]: string }>): string => {
  const array = typeof objArray !== 'object' ? JSON.parse(objArray) : objArray;
  let str = '';

  for (let i = 0; i < array.length; i++) {
    let line = '';
    for (const index in array[i]) {
      if (line !== '') line += ',';

      line += array[i][index];
    }
    str += line + '\r\n';
  }
  return str;
};
